import type {
  RevolveCutPreviewFragment,
  RevolveFeatureEditDefinition,
} from '@puppycad/cad-core';

/**
 * 克隆旋转切除保留体片段键。
 *
 * 设计说明：
 * 1. 该值可能来自 web-client 的 Vue ref 对象；
 * 2. Vue proxy 不能直接参与 structuredClone；
 * 3. 这里用显式遍历复制纯字符串数组，避免 UI 响应式实现泄漏到 cad-core。
 *
 * @param selection 原始选择。
 * @returns 克隆后的选择。
 */
export function cloneRevolveCutKeepShapeKeys(
  selection: Record<string, string[]> = {},
): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(selection).map(([solidId, shapeKeys]) => [solidId, [...shapeKeys]]),
  );
}

/**
 * 构建传入 cad-core 的旋转定义纯数据快照。
 *
 * 设计说明：
 * 1. `revolveDefinition` 属于 web-client 会话状态，不能把 Vue ref/proxy 直接传入 cad-core；
 * 2. cad-core 会对 FeatureDefinition 做结构化克隆，Vue proxy 会触发 DataCloneError；
 * 3. 这里显式复制旋转定义字段，确保跨包边界只传纯数据。
 *
 * @param definition 当前 UI 定义。
 * @returns 纯数据定义快照。
 */
export function buildCadCoreRevolveDefinitionSnapshot(
  definition: RevolveFeatureEditDefinition,
): RevolveFeatureEditDefinition {
  return {
    direction1Angle: Number(definition.direction1Angle),
    direction2Enabled: definition.direction2Enabled === true,
    direction2Angle: Number(definition.direction2Angle ?? 0),
    directionReversed: definition.directionReversed === true,
    mergeResult: definition.mergeResult !== false,
    flipSideToCut: definition.flipSideToCut === true,
    keepShapeKeys: cloneRevolveCutKeepShapeKeys(definition.keepShapeKeys ?? {}),
    previewToken: typeof definition.previewToken === 'string' ? definition.previewToken : undefined,
  };
}

/**
 * 根据预览片段和旧选择归一化保留体选择。
 *
 * @param fragments 预览片段。
 * @param previousSelection 旧选择。
 * @returns 下一轮选择。
 */
export function normalizeRevolveCutKeepShapeKeys(
  fragments: RevolveCutPreviewFragment[],
  previousSelection: Record<string, string[]>,
): Record<string, string[]> {
  const previous = new Map(
    Object.entries(previousSelection).map(([solidId, keys]) => [solidId, new Set(keys)]),
  );
  const next = new Map<string, string[]>();
  fragments.forEach((fragment) => {
    const current = next.get(fragment.sourceSolidId) ?? [];
    const previousKeys = previous.get(fragment.sourceSolidId);
    if (!previousKeys || previousKeys.has(fragment.shapeKey)) {
      current.push(fragment.shapeKey);
    }
    next.set(fragment.sourceSolidId, current);
  });
  return Object.fromEntries(next);
}
